import axios from 'axios';
import Animal from '../Entities/Animal';
import { API_URL } from '../constants';
import Location from '../Entities/Location';
import * as Sentry from '@sentry/browser';

class EndPoint<T, Q> {
  url: string;
  Entity: { new (obj: any): T } | null;

  constructor({ apiUrl, route }: { apiUrl: string; route: string }, Entity?: { new (obj: any): T }) {
    this.url = `${apiUrl}/${route}`;
    this.Entity = Entity ? Entity : null;
  }

  addSentryBreadcrumb = (method: string, params = {}) => {
    Sentry.addBreadcrumb({
      category: 'REQUEST',
      message: `${method.toUpperCase().trim()}_REQUEST`,
      level: Sentry.Severity.Info,
      data: {
        'params/data': JSON.stringify(params),
        url: this.url,
      },
    });
  };

  async get(params: PaginationParams): Promise<PaginatedResponse<T>>;
  async get(params?: Q): Promise<T[]>;
  async get(params?: Q | PaginationParams): Promise<T[] | PaginatedResponse<T> | T> {
    this.addSentryBreadcrumb('GET', params);
    const { data } = await axios.get(this.url, {
      params,
    });

    // If we don't have an entity for this...
    if (!this.Entity || !data) {
      return data;
    }

    data as any[]; // eslint-disable-line no-unused-expressions

    const Entity = this.Entity as { new (obj: any): T };

    if (data.docs && data.pages) {
      data.docs = data.docs.map((doc: any) => new Entity(doc));
      return data;
    }

    return data.map((d: any) => new Entity(d));
  }

  async getOne(identifier: string | number, params?: { [key: string]: any }): Promise<T> {
    this.addSentryBreadcrumb('GET_ONE', params);
    const { data } = await axios.get(`${this.url}/${identifier}`, {
      params,
    });

    // If we don't have an entity for this...
    if (!this.Entity || !data) {
      return data;
    }

    const Entity = this.Entity as { new (obj: any): T };
    return new Entity(data);
  }

  async post(data: { [key: string]: any }) {
    this.addSentryBreadcrumb('POST', data);
    const result = await axios.post(this.url, data);
    if (!this.Entity) {
      return result.data;
    }
    return new this.Entity(result.data);
  }

  patch(identifier: string | number, data: { [key: string]: any }) {
    this.addSentryBreadcrumb('PATCH', data);
    return axios.patch(`${this.url}/${identifier}`, data);
  }

  put(identifier: string, data: { [key: string]: any }) {
    this.addSentryBreadcrumb('PUT', data);
    return axios.put(`${this.url}/${identifier}`, data);
  }

  delete(identifier: string | number, data?: { [key: string]: any }) {
    this.addSentryBreadcrumb('DELETE', data);
    return axios.delete(`${this.url}/${identifier}`, data);
  }

  distincts(params: Q) {
    this.addSentryBreadcrumb('DISTINCTS', params);
    return axios.get(`${this.url}/distincts`, { params });
  }
}

class Api {
  private url: string;

  animals: EndPoint<
    Animal,
    {
      active?: boolean;
      animalType?: 'cat' | 'dog';
      location?: string;
    }
  >;

  specialNeeds: EndPoint<SpecialNeed, { animalType?: 'dog' | 'cat' }>;

  enrichmentData: EndPoint<
    Enrichment,
    {
      startDate: string; // ISO string
      endDate: string; // ISO string
      aNumber?: Ianimal['aNumber'];
      animalType?: 'cat' | 'dog';
      filters?: string;
      exludeTimeMax?: number;
    }
  >;

  comments: EndPoint<
    Comment,
    {
      aNumber: Ianimal['aNumber'];
      startDate: string; // ISO string
      endDate: string; // ISO string
    }
  >;

  locations: EndPoint<Location, {}>;

  medicalData: EndPoint<
    MedicalData,
    {
      startDate: string; // ISO string
      endDate: string; // ISO string
      aNumber?: Ianimal['aNumber'];
    }
  >;

  assessment: EndPoint<null, {}>;

  evaluations: EndPoint<Evaluation, {}>;

  customContent: EndPoint<
    CustomContent,
    {
      name: string;
    }
  >;

  constructor() {
    this.url = API_URL;
    this.animals = this.createEndPoint('animals', Animal);
    this.enrichmentData = this.createEndPoint('enrichmentData');
    this.comments = this.createEndPoint('comments');
    this.specialNeeds = this.createEndPoint('specialNeeds');
    this.locations = this.createEndPoint('locations');
    this.medicalData = this.createEndPoint('medicalData');
    this.evaluations = this.createEndPoint('evaluations');
    this.assessment = this.createEndPoint('assessment');
    this.customContent = this.createEndPoint('customContent');
  }

  private createEndPoint(route: string, Entity?: { new (obj: any): any }) {
    return new EndPoint(
      {
        route,
        apiUrl: this.url,
      },
      Entity,
    );
  }
}

export default new Api();
